// =============================================================================
// Composant Visites AMO — vue calendrier et vue liste
// =============================================================================

import { Calendar, MapPin, Phone, Building2, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { formatClientAddress } from "../../utils/clientFormat";

// Couleurs visites AMO
const VISITE_COLORS = {
  green: { bg: "bg-green-500/30", border: "border-green-500", text: "text-green-300", label: "Contrat signé" },
  emerald: { bg: "bg-emerald-500/30", border: "border-emerald-500", text: "text-emerald-300", label: "Visite payée" },
  purple: { bg: "bg-purple-500/30", border: "border-purple-500", text: "text-purple-300", label: "Payé" },
  blue: { bg: "bg-blue-500/30", border: "border-blue-500", text: "text-blue-300", label: "En cours" },
  grayed: { bg: "bg-zinc-700/20", border: "border-zinc-600", text: "text-zinc-500", label: "Autre installateur" },
};

function isMyVisite(visite, currentUserName) {
  if (!currentUserName) return true;
  const inst = (visite.installateur || "").toLowerCase();
  const me = currentUserName.toLowerCase();
  return inst.includes(me) || me.includes(inst);
}

export default function VisitesAMOView({ visites, filterVisiteur, viewMode, currentMonth, onSelectVisite, isExternal, currentUserName }) {
  const filteredVisites = filterVisiteur
    ? visites.filter(v => v.visiteur === filterVisiteur)
    : visites;

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
  const visitesMois = filteredVisites.filter(v => v.date?.startsWith(monthStr));

  const visitesByDate = visitesMois.reduce((acc, visite) => {
    const date = visite.date;
    if (!date) return acc;
    if (!acc[date]) acc[date] = [];
    acc[date].push(visite);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    const startWeekday = firstDay.getDay() || 7;
    for (let i = startWeekday - 1; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      days.push({ date, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const date = new Date(year, month + 1, i);
        days.push({ date, isCurrentMonth: false });
      }
    }

    return days;
  };

  const getVisitesForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return filteredVisites.filter(v => v.date === dateStr);
  };

  if (viewMode === "calendar") {
    const days = getDaysInMonth();
    const today = new Date().toISOString().split("T")[0];

    return (
      <div className="bg-slate-800/30 rounded-xl p-4" data-testid="visites-amo-calendar">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-1 mb-2">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => (
            <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-1">
          {days.map(({ date, isCurrentMonth }, idx) => {
            const dateStr = date.toISOString().split("T")[0];
            const dayVisites = getVisitesForDate(date);
            const isToday = dateStr === today;

            return (
              <div
                key={idx}
                className={`min-h-[90px] p-1 rounded-lg border ${
                  isCurrentMonth ? "border-slate-700 bg-slate-800/50" : "border-transparent bg-slate-900/30"
                } ${isToday ? "ring-2 ring-orange-500" : ""}`}
              >
                <div className={`text-xs font-medium mb-1 ${isCurrentMonth ? "text-slate-300" : "text-slate-600"}`}>
                  {date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayVisites.slice(0, 3).map((visite, i) => {
                    const isMine = !isExternal || isMyVisite(visite, currentUserName);
                    const colorInfo = isMine
                      ? (VISITE_COLORS[visite.color] || VISITE_COLORS.blue)
                      : VISITE_COLORS.grayed;
                    return (
                      <div
                        key={i}
                        onClick={() => isMine && onSelectVisite(visite)}
                        className={`${colorInfo.bg} ${colorInfo.border} border-l-2 rounded-r px-1 py-0.5 text-xs truncate ${isMine ? "cursor-pointer hover:opacity-80" : "cursor-default opacity-60"}`}
                        title={isMine ? `${visite.client_nom} - ${visite.visiteur || "Non assigné"}` : "Occupé"}
                      >
                        <span className={colorInfo.text}>{isMine ? (visite.client_nom?.split(" ")[0] || "Client") : "Occupé"}</span>
                      </div>
                    );
                  })}
                  {dayVisites.length > 3 && (
                    <div className="text-xs text-slate-500 pl-1">+{dayVisites.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Vue liste
  return (
    <div className="space-y-4" data-testid="visites-amo-list">
      {Object.keys(visitesByDate).length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg">
          <Calendar className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
          <p className="text-slate-400 text-lg">Aucune visite ce mois</p>
          <p className="text-zinc-400 mt-2">Les visites planifiées s'afficheront ici</p>
        </div>
      ) : (
        Object.entries(visitesByDate)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, dayVisites]) => (
            <div key={date} className="space-y-2">
              <h3 className="text-slate-400 text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(date)}
                <Badge className="bg-slate-700 text-xs">{dayVisites.length}</Badge>
              </h3>

              {dayVisites.map((visite) => {
                const isMine = !isExternal || isMyVisite(visite, currentUserName);
                const colorInfo = isMine
                  ? (VISITE_COLORS[visite.color] || VISITE_COLORS.blue)
                  : VISITE_COLORS.grayed;

                if (!isMine) {
                  return (
                    <div key={visite.id}
                      className={`${colorInfo.bg} border-l-4 ${colorInfo.border} rounded-r-lg p-4 opacity-50`}>
                      <div className="flex items-center gap-3">
                        <span className={`font-medium ${colorInfo.text}`}>Occupé</span>
                        <Badge className="bg-zinc-700/30 text-zinc-500 text-xs">Autre installateur</Badge>
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
                        {visite.visiteur && `Visiteur: ${visite.visiteur}`}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={visite.id}
                    onClick={() => onSelectVisite(visite)}
                    className={`${colorInfo.bg} border-l-4 ${colorInfo.border} rounded-r-lg p-4 cursor-pointer hover:bg-opacity-50 transition-colors`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`font-medium ${colorInfo.text}`}>{visite.client_nom}</span>
                          <Badge className={`${colorInfo.bg} ${colorInfo.text} text-xs`}>
                            {colorInfo.label}
                          </Badge>
                          {visite.visiteur && (
                            <Badge className="bg-orange-500/20 text-orange-300 text-xs">
                              {visite.visiteur}
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-400">
                          {visite.client_adresse && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="truncate">
                                {formatClientAddress({ adresse: visite.client_adresse })}
                              </span>
                            </div>
                          )}
                          {visite.client_telephone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span>{visite.client_telephone}</span>
                            </div>
                          )}
                          {visite.installateur && (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              <span>{visite.installateur}</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-2 text-xs text-slate-500">
                          Réf: {visite.référence} • Statut: {visite.statut}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); onSelectVisite(visite); }}
                          className="text-orange-400 hover:text-orange-300 h-8 w-8 p-0"
                          title="Ouvrir le dossier"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
      )}
    </div>
  );
}
